/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type CreateWaitlistPayload,
  type UpdateWaitlistPayload,
  type WaitlistEntry,
  type WaitlistStatus,
  waitlistApi,
} from '../api/waitlist.js';

const KEY = ['waitlist'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useWaitlist(status?: WaitlistStatus): UseQueryResult<readonly WaitlistEntry[]> {
  return useQuery({ queryKey: [...KEY, status ?? 'all'], queryFn: () => waitlistApi.list(status) });
}

export function useCreateWaitlistEntry(): UseMutationResult<
  WaitlistEntry,
  Error,
  CreateWaitlistPayload
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: CreateWaitlistPayload) => waitlistApi.create(p),
    onSuccess: invalidate,
  });
}

export function useUpdateWaitlistEntry(): UseMutationResult<
  WaitlistEntry,
  Error,
  { id: string; payload: UpdateWaitlistPayload }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWaitlistPayload }) =>
      waitlistApi.update(id, payload),
    onSuccess: invalidate,
  });
}
